import { useEffect, useState, useCallback, useRef } from "react";
import { IPaginatedProps } from "../types";
import { getDomainPaginatedList } from "../services/domainsService";

interface IUseFetchDomains extends IPaginatedProps {
  searchTerm?: string;
  statusFilter?: string; // Changed from isActive to statusFilter (values: "Active", "Inactive", "All")
}

const useFetchPaginatedDomain = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  statusFilter = "Active", // Changed from isActive = "true" to statusFilter = "Active"
}: IUseFetchDomains) => {
  // Map statusFilter to isActive for API call
  const isActive =
    statusFilter === "Active"
      ? "true"
      : statusFilter === "Inactive"
      ? "false"
      : "all";
  const [domainData, setDomainData] = useState<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result: any[] = await getDomainPaginatedList({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setDomainData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setDomainData([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [domainData, fetchData];
};

export default useFetchPaginatedDomain;
